// @ts-strict-ignore
import {reporter} from 'common/report/error-reporting';
import {DATASET_MIXPANEL, ResourceType} from 'common/report/constants';
import type {EngagePropertiesResponse} from 'common/report/queries/query-api/types';
import {LocalizationStore} from 'common/i18n/store';

import {queryApiUrl} from '../utils';
import {BaseClient} from '../utils/base-client';
import {processProperties} from './utils';

export class PeoplePropertiesClient extends BaseClient {
  declare i18n: LocalizationStore;

  constructor(i18n: LocalizationStore) {
    super();
    this.i18n = i18n;
  }

  buildEndpoint() {
    return queryApiUrl(`engage/properties`);
  }

  buildQueryParams(options = {}) {
    const {
      projectId,
      datasetId,
      profileTable,
      dataGroupId,
      includeCustom,
      objectProperty,
      property,
      includeDimensionProperties,
      workspaceId,
      useQueryCache,
    } = {
      projectId: null,
      datasetId: DATASET_MIXPANEL,
      profileTable: null,
      dataGroupId: null,
      objectProperty: null,
      property: null,
      includeCustom: false,
      includeDimensionProperties: null,
      workspaceId: null,
      useQueryCache: null,
      ...options,
    };
    /* eslint-disable camelcase */
    return {
      project_id: projectId,
      ...(datasetId !== DATASET_MIXPANEL ? {dataset_name: datasetId} : {}),
      // TODO(RNA-183): Remove once profileTypes are removed from Insights.
      ...(profileTable && !dataGroupId ? {where: `properties["Table"] == "${profileTable}"`} : {}),
      ...(dataGroupId ? {data_group_id: dataGroupId} : {}),
      ...(includeCustom ? {include_custom: includeCustom} : {}),
      ...(includeDimensionProperties ? {include_dimension_props: includeDimensionProperties} : {}),
      ...(objectProperty ? {object_property: JSON.stringify(objectProperty)} : {}),
      ...(property ? {property: JSON.stringify(property)} : {}),
      ...(workspaceId ? {workspace_id: workspaceId} : {}),
      ...(useQueryCache !== null ? {use_query_cache: useQueryCache} : {}),
    };
    /* eslint-enable camelcase */
  }

  processResponse(response: EngagePropertiesResponse, params) {
    if (response.status === `ok`) {
      try {
        // Getting empty keys was having downstream impacts and will be filtered out here
        // see https://mixpanel.slack.com/archives/C07KZ4NBB/p1605105643087200
        const sanitizedResults = Object.fromEntries(
          Object.entries(response.results).filter(([key, value]) => !!key && !!value),
        );
        return processProperties(sanitizedResults, ResourceType.User, this.i18n, {dataGroupId: params.dataGroupId});
      } catch (err) {
        reporter.error(err);
      }
    } else {
      if (!response.error.includes(`Your account is blocked because payment is required`)) {
        reporter.error(`Unsuccessful PeoplePropertiesClient response ${response.error}`, response);
      }
    }
    return [];
  }
}
